import { useState } from 'react';
import { Link, NavLink, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Dashboard from './pages/Dashboard';
import PackageDetail from './pages/PackageDetail';
import PublicBidPage from './pages/PublicBidPage';
import MyFindings from './pages/MyFindings';
import MyTasks from './pages/MyTasks';
import PmStandards from './pages/PmStandards';
import PmStandardsDetail from './pages/PmStandardsDetail';
import PortalDashboard from './pages/PortalDashboard';
import PortalEngagement from './pages/PortalEngagement';
import PortalLogin from './pages/PortalLogin';
import Login from './pages/Login';
import Admin from './pages/Admin';
import IssuesQueue from './pages/IssuesQueue';
import ComplianceQueue from './pages/ComplianceQueue';
import MyInstructions from './pages/MyInstructions';
import Suppliers from './pages/Suppliers';
import ReportButton from './components/ReportButton';
import TaskFab from './components/TaskFab';
import TaskPanel from './components/TaskPanel';
import { Toaster } from './components/ui/Toast';
import NewVersionBanner from './components/NewVersionBanner';
import { getMe, logout, exitPortalPreview, toolFeedbackOpenCount, myVoteRounds, pendingVoteCount, staffComplianceQueue, portalTodoCount, myWorkInstructionCount, ROLE_LABEL, type MeResponse } from './lib/api';

/**
 * Which notification pipes are actually live — staff only, and honest about all three.
 *
 * There are now three ways the system can reach a colleague: 企业微信 fan-out, the wtl-chat bot
 * (催办/完成 pings) and the §11 workbench task. On prod all three have been dark at one time or another,
 * and the old single "WeCom 未配置" chip said nothing about the other two — so 预算 could press 催一下,
 * see 已催 3 人, and believe someone had been told. This chip is the ambient version of that truth.
 */
function NotifyPill({ me }: { me: MeResponse }) {
  const wecom = !!me.wecom_outbound_configured;
  const chat = !!me.mm_notify_configured;
  const task = !!me.task_platform_configured;
  const title = '企业微信 WECOM_GATEWAY_BASE / PARTNER_INBOUND_TOKEN / WECOM_GATEWAY_STAFF_PATH'
    + ' · 聊天 MM_NOTIFY_URL / MM_NOTIFY_SECRET · 任务 VOCATASK_*'
    + ' —— 未配置的通道只写入审计记录，不会真正发出通知 / an unconfigured channel is recorded, never delivered';
  if (!wecom && !chat && !task) {
    return <span className="pill pill-warn" title={title}>通知未配置 / notify: dev</span>;
  }
  const ok = (b: boolean) => (b ? '✓' : '✗');
  return <span className="pill pill-muted" title={title}>通知：企业微信 {ok(wecom)} · 聊天 {ok(chat)} · 任务 {ok(task)}</span>;
}

/** Current-account chip + logout, or a Sign-in link. Passcode login lives on /login (DR-A). */
function AuthBar() {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const [busy, setBusy] = useState(false);

  async function doLogout() {
    setBusy(true);
    try { await logout(); await qc.invalidateQueries(); } finally { setBusy(false); }
  }

  // Staff "view as labor": read-only preview session — a loud banner + exit instead of the normal chrome.
  if (me?.authenticated && me.account?.view_only) {
    return (
      <span className="row small" style={{ gap: 8, alignItems: 'center' }}>
        <span className="pill pill-warn" title="您正以该劳务公司的视角预览（只读）— 不能代其报价或确认">
          👁 预览中（只读）· {me.account.display_name} / Read-only preview
        </span>
        <button className="btn-primary" disabled={busy} onClick={async () => {
          setBusy(true);
          try { const r = await exitPortalPreview(); await qc.invalidateQueries(); window.location.href = r.redirect; }
          catch { setBusy(false); }
        }}>← 返回员工端 / Back to staff</button>
      </span>
    );
  }
  if (me?.authenticated && me.account) {
    return (
      <span className="row small" style={{ gap: 8, alignItems: 'center' }}>
        <span className="pill pill-stage">
          {me.account.contact_name ? `${me.account.display_name} · ${me.account.contact_name}` : me.account.display_name} · {ROLE_LABEL[me.account.role] ?? me.account.role}
        </span>
        {me.account.subject_type === 'staff' && <NotifyPill me={me} />}
        <button disabled={busy} onClick={doLogout}>退出 / Logout</button>
      </span>
    );
  }
  return <NavLink to="/login" className="nav-link">登录 / Sign in</NavLink>;
}

/**
 * 我的任务 nav link with a 待推荐/待投 badge — internal staff only.
 *
 * The blocking finding behind it: a listed voter had NO ambient signal that a round was waiting on
 * them. WeCom pings are dark on prod and 我的任务 is not where a PM lives, so an open round could sit
 * for days blocking 邀请劳务. Mirrors IssuesNavLink; polls the same query 我的任务 already uses, so the
 * page and the badge share one cache entry.
 */
function TasksNavLink() {
  const { data } = useQuery({ queryKey: ['my-vote-rounds'], queryFn: myVoteRounds, refetchInterval: 60000 });
  const n = pendingVoteCount(data?.rounds);
  const nominating = (data?.rounds ?? []).some((r) => r.kind === 'nominate');
  return (
    <NavLink to="/tasks" className="nav-link">
      我的任务 / My tasks
      {n > 0 && (
        <span className="pill pill-warn small" style={{ marginLeft: 6 }}
          title={`${nominating ? '待推荐/待投' : '待投'}定标投票 ${n} 项 —— 票投齐前该标无法邀请劳务 / ${n} vote round(s) waiting on you; the tender is blocked until they are in`}>
          {nominating ? '待推荐' : '待投'} {n}
        </span>
      )}
    </NavLink>
  );
}

/**
 * 合规待审 nav link with a 待审 badge — internal staff only (Eric 9/18, compliance file).
 *
 * Same reason as TasksNavLink: an uploaded 资质 has no ambient signal at all otherwise — nobody opens a
 * package to discover that one arrived. For a 项目经理 this link is ALSO their only door into the
 * 履约档案, since /packages/:pid is closed to them (it carries pricing). `mine` follows the page's own
 * default, so the badge counts exactly what the page will show them.
 */
function ComplianceNavLink({ isPm }: { isPm: boolean }) {
  const { data } = useQuery({
    queryKey: ['compliance', 'queue', { mine: isPm, category: '' }],
    queryFn: () => staffComplianceQueue({ mine: isPm }),   // counts are computed server-side regardless of the row filter — no need to pull the whole ledger every 60 s
    refetchInterval: 60000,
  });
  const n = data?.counts.uploaded ?? 0;
  return (
    <NavLink to="/compliance" className="nav-link">
      合规待审 / Compliance
      {n > 0 && (
        <span className="pill pill-bad small" style={{ marginLeft: 6 }}
          title={`${n} 项履约资料已上传待审 / ${n} compliance document(s) waiting for review`}>{n}</span>
      )}
    </NavLink>
  );
}

/**
 * 工程指令 nav link with a 待办 badge — internal staff only (Eric 9/18, 工程指令).
 *
 * Same argument as ComplianceNavLink, and sharper: an 工程指令 waiting on a signature BLOCKS the order
 * from existing at all (9.1.1), and the contract then says unwritten work is neither measured nor paid.
 * WeCom is dark on prod, so this badge is the only thing that tells a 专业工程师 a signature is owed.
 * `?count=1` answers with a single integer — the poll costs nothing next to the full queue.
 */
function InstructionsNavLink() {
  const { data } = useQuery({
    queryKey: ['my-work-instructions', 'count'],
    queryFn: myWorkInstructionCount,
    refetchInterval: 60000,
  });
  const n = data?.count ?? 0;
  return (
    <NavLink to="/me/instructions" className="nav-link">
      工程指令 / Instructions
      {n > 0 && (
        <span className="pill pill-warn small" style={{ marginLeft: 6 }}
          title={`${n} 项工程指令待您处理（签字/定价/批准/核实）/ ${n} work instruction(s) waiting on you`}>{n}</span>
      )}
    </NavLink>
  );
}

/**
 * 我的投标 nav link with a 履约 待办 badge — labor companies only.
 *
 * Eric 9/18 (compliance file): until the Step 5 sweep ships, this badge is the ONLY live reminder
 * channel a contractor has (WeCom pushes are dark on prod, the thread digest isn't built yet). So it
 * polls like TasksNavLink does, and its tooltip names the projects — 江卫春 runs several at once and
 * 「待办 7」 alone would not tell him which site to chase.
 */
function PortalNavLink() {
  const { data } = useQuery({ queryKey: ['portal-todo-count'], queryFn: portalTodoCount, refetchInterval: 60000 });
  // Eric 9/18 (工程指令, PR9): ONE number, two sources. Layer 1's `total` is the 履约档案 count; the five
  // 工程指令 keys are what a contractor owes on the orders themselves. A contractor does not think in
  // layers — 「待办 N」 has to mean "N things are waiting on me", or the badge trains them to ignore it.
  // The tooltip is where the two are separated, because 江卫春 runs several sites and 「待办 7」 alone
  // would not tell him which one to chase.
  const wi = (data?.instructions_unread ?? 0) + (data?.quotes_due_soon ?? 0) + (data?.quotes_overdue ?? 0)
    + (data?.daywork_due_today ?? 0) + (data?.confirmations_pending ?? 0);
  const n = (data?.total ?? 0) + wi;
  const lines: string[] = [];
  if ((data?.by_engagement ?? []).length) {
    lines.push(`履约待办：${(data!.by_engagement).map((e) => `${e.package_name ?? '项目'} ${e.n} 项`).join('　· ')} / compliance documents outstanding`);
  }
  if (wi > 0) {
    const bits: string[] = [];
    if (data?.instructions_unread) bits.push(`未读指令 ${data.instructions_unread}`);
    if (data?.quotes_due_soon) bits.push(`报价临期 ${data.quotes_due_soon}`);
    if (data?.quotes_overdue) bits.push(`报价逾期 ${data.quotes_overdue}`);
    if (data?.daywork_due_today) bits.push(`计日工待报 ${data.daywork_due_today}`);
    if (data?.confirmations_pending) bits.push(`待认价 ${data.confirmations_pending}`);
    lines.push(`工程指令：${bits.join('　· ')} / work instructions outstanding`);
  }
  const title = lines.length ? lines.join('\n') : undefined;
  return (
    <NavLink to="/portal" className="nav-link">
      我的投标 / My bids
      {n > 0 && <span className={`pill ${wi > 0 ? 'pill-bad' : 'pill-warn'} small`} style={{ marginLeft: 6 }} title={title}>待办 {n}</span>}
    </NavLink>
  );
}

/** Issues nav link with an open-count badge — internal staff only. */
function IssuesNavLink() {
  const { data } = useQuery({ queryKey: ['tool-feedback', 'open-count'], queryFn: toolFeedbackOpenCount });
  const n = data?.count ?? 0;
  return (
    <NavLink to="/issues" className="nav-link">
      🐞 反馈 / Issues{n > 0 && <span className="pill pill-bad small" style={{ marginLeft: 6 }}>{n}</span>}
    </NavLink>
  );
}

function ChromeLayout() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const isPm = me?.account?.role === 'site_pm';
  const isLaborCo = me?.account?.role === 'labor_co';
  const isInternal = me?.account?.subject_type === 'staff';
  const [panelOpen, setPanelOpen] = useState(false);
  // Since the security sweep every staff read needs a session — an anonymous visitor sees empty lists
  // and 401s on writes (Eric 7/7, retired-intranet-URL confusion). Say it plainly instead.
  const { pathname } = useLocation();
  const onLoginPage = pathname === '/login' || pathname.startsWith('/portal/login');
  const showAnonBanner = !!me && !me.authenticated && !onLoginPage;
  return (
    <div>
      {showAnonBanner && (
        <div style={{ background: 'var(--warn-soft)', borderBottom: '1px solid var(--warn)', padding: '8px 16px', fontSize: 14 }}>
          ⚠ 未登录 — 数据不可见、操作会被拒绝。请先 <Link to="/login"><strong>登录（员工）</strong></Link> 或 <Link to="/portal/login"><strong>登录（劳务）</strong></Link>。
          / Not signed in — data is hidden and actions will fail. Please sign in first.
        </div>
      )}
      <header className="app-header">
        <h1><Link to="/" style={{ color: 'inherit' }}>劳务承包 <span className="v">Labor Procurement</span></Link></h1>
        <nav className="row small" style={{ gap: 12, alignItems: 'center', marginLeft: 'auto' }}>
          {isPm && <NavLink to="/pm/standards" className="nav-link">验收标准 / Standards</NavLink>}
          {isPm && <NavLink to="/my-findings" className="nav-link">我的整改 / My findings</NavLink>}
          {isInternal && <TasksNavLink />}
          {isInternal && <NavLink to="/suppliers" className="nav-link">供应商 / Suppliers</NavLink>}
          {isInternal && <ComplianceNavLink isPm={isPm} />}
          {isInternal && <InstructionsNavLink />}
          {/* gm: full access admin; budget: the doc-templates panel only (合同/验收标准确认书 模板) */}
          {(me?.account?.role === 'gm' || me?.account?.role === 'budget') && <NavLink to="/admin" className="nav-link">管理 / Admin</NavLink>}
          {isLaborCo && <PortalNavLink />}
          {isInternal && <IssuesNavLink />}
          <AuthBar />
        </nav>
      </header>
      <div className="wrap"><Outlet /></div>
      {/* Floating 🐞 Report button — any signed-in user (staff + labor portal) can file a bug/feature.
          The triage queue (/issues) stays internal-staff-only. */}
      {(isInternal || isLaborCo) && <ReportButton />}
      {/* 📋 Delegate / 🗂 Tasks — internal staff only. The panel is the in-app half of "the agent = Both". */}
      {isInternal && <TaskFab onOpenPanel={() => setPanelOpen(true)} />}
      {isInternal && <TaskPanel open={panelOpen} onClose={() => setPanelOpen(false)} />}
      <Toaster />
    </div>
  );
}

/** A labor-co lands on the portal; a site PM on their standards desk (the package browser carries
 *  pricing, which site_pm can no longer see — 改改 7/14 lockdown); other staff/anon see the dashboard. */
function Home() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  if (me?.account?.role === 'labor_co') return <Navigate to="/portal" replace />;
  if (me?.account?.role === 'site_pm') return <Navigate to="/pm/standards" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <>
    {/* 改改 9/21 (fb_a50fe09d) · a tab left open across a deploy runs the OLD bundle — say so, on every route
        (staff, labor portal, public bid page), and offer the refresh. */}
    <NewVersionBanner />
    <Routes>
      <Route element={<ChromeLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/packages/:pid" element={<PackageDetail />} />
        <Route path="/my-findings" element={<MyFindings />} />
        <Route path="/pm/standards" element={<PmStandards />} />
        <Route path="/pm/standards/:wid" element={<PmStandardsDetail />} />
        <Route path="/tasks" element={<MyTasks />} />
        <Route path="/issues" element={<IssuesQueue />} />
        <Route path="/compliance" element={<ComplianceQueue />} />
        <Route path="/me/instructions" element={<MyInstructions />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal" element={<PortalDashboard />} />
        <Route path="/portal/engagements/:eid" element={<PortalEngagement />} />
      </Route>
      {/* PublicBidPage lives outside ChromeLayout, so it carries its own Toaster. */}
      <Route path="/p/bid/:token" element={<><PublicBidPage /><Toaster /></>} />
    </Routes>
    </>
  );
}
